import { useEffect, useState, useContext } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { FirebaseContext } from '../components/Firebase';
import { useOrganizationStore } from '../store/organization.store';

/**
 * Shape of a /customModules/{moduleId} document.
 *
 * Each doc represents one bespoke, per-client module (e.g. the Checkers
 * chase list consolidator). Access is gated per-org via
 * `authorisedOrgIds` — orgs not listed there don't see the module in
 * the nav bar and get redirected if they try to visit its route.
 *
 * Admin UI at /admin/modules is where these docs get edited.
 */
export interface CustomModule {
  /** Doc id, also used in the route path (`/modules/{id}`). */
  id: string;
  /** Human-readable module name shown in the nav bar + admin page. */
  name: string;
  /** One-liner about what the module does — surfaced in admin UI. */
  description?: string;
  /** Route path this module renders at. Stored on the doc so the nav
   *  can build links without importing every module id. */
  route: string;
  /** Org ids allowed to see + use this module. Empty array = nobody. */
  authorisedOrgIds: string[];
  createdAt?: number;
  updatedAt?: number;
}

interface UseCustomModuleAccess {
  /** All modules the current org is authorised for. Refreshes live via
   *  a Firestore snapshot listener. Empty array while loading or when
   *  the org has no authorised modules. */
  modules: CustomModule[];
  /** Whether the initial snapshot has resolved. `modules` is empty
   *  during the loading window; consumers gate nav / route rendering
   *  on this to avoid a flash of un-permissioned content. */
  isLoaded: boolean;
  /** True when the current org has access to at least one module.
   *  Convenience derived value for nav-bar visibility. */
  hasAnyAccess: boolean;
}

/**
 * Subscribes to /customModules and returns the subset visible to the
 * current org. Empty result when the org has no authorised modules —
 * that's how the "Modules" nav tab decides whether to render.
 *
 * The Firestore rule allows any authenticated user to read the
 * collection so client-side filtering is cheap; membership check runs
 * against `authorisedOrgIds` for the current org id from the org store.
 */
export function useCustomModuleAccess(): UseCustomModuleAccess {
  const firebase = useContext(FirebaseContext) as any;
  const currentOrgId = useOrganizationStore((s) => s.currentOrgId);

  const [modules, setModules] = useState<CustomModule[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!firebase || !currentOrgId) {
      setModules([]);
      setIsLoaded(true);
      return;
    }

    setIsLoaded(false);
    const ref = collection(firebase.db, 'customModules');
    const unsubscribe = onSnapshot(
      ref,
      (snap: any) => {
        const authorised: CustomModule[] = [];
        snap.forEach((doc: any) => {
          const data = doc.data() as CustomModule;
          const ids = Array.isArray(data.authorisedOrgIds) ? data.authorisedOrgIds : [];
          if (ids.includes(currentOrgId)) {
            authorised.push({ ...data, id: doc.id });
          }
        });
        // Stable ordering — sort by display name so the nav tab menu
        // doesn't reshuffle when Firestore returns docs in a different
        // order on refresh.
        authorised.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setModules(authorised);
        setIsLoaded(true);
      },
      (err: any) => {
        console.error('useCustomModuleAccess: subscription failed:', err);
        setModules([]);
        setIsLoaded(true);
      },
    );

    return () => unsubscribe();
  }, [firebase, currentOrgId]);

  return {
    modules,
    isLoaded,
    hasAnyAccess: modules.length > 0,
  };
}
